import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';

import { useModo } from '../config/modo';
import { FAMILIAS, CODIGOS_33, countByTeaching } from '../config/ensenanzas33';
import { useCatalog, useTeachings } from '../content/hooks';
import { formatoTitulo } from '../config/titulos';
import CabeceraSeccion from '../components/CabeceraSeccion';
import './fundacion.css';
import './comoleer.css';
import './biblioteca.css';
import './treintaytres.css';

/**
 * «33» — la página del número de la Fundación.
 *
 * Faina, 14-sep: «Quiero agregar ahora una página al menú, que se va a llamar
 * 33. Esta página va a informar acerca de lo que significa y ha significado el
 * número 33 en el mundo (…) y desde ese mismo lugar desplegaremos las 33
 * enseñanzas de la humanidad.» Va en el menú a continuación de Ángeles y
 * Arcángeles.
 *
 * Dos bloques: el 33 en el mundo (las tarjetas, sin rotulo) · las 33
 * enseñanzas, que abren la Biblioteca filtrada por su código.
 *
 * 🟥 LA REGLA DE LA VERDAD RIGE AQUÍ IGUAL QUE EN LA PORTADA. Cada dato del
 * bloque «en el mundo» se verificó el 14-sep (2 Samuel 5:5 · Lucas 3:23 · el
 * Rig Veda · Trāyastriṃśa · Lag BaOmer · la coronilla del Sagrado Corazón de
 * Jesús (33 cuentas; oraciones del P. Croiset, S. J.) · Dante · el Rito
 * Escocés · las 33 vértebras · el paralelo 33 · el LP de
 * Columbia de 1948 · la mina San José). Lo que es creencia —la numerología, el
 * «número maestro»— va en su propia tarjeta y dicho como creencia. Se dejaron
 * fuera, por no poder sostenerse, «los 33 milagros de Jesús», «el nombre de
 * Dios 33 veces en el Génesis» y lo de egipcios y mayas.
 *
 * Los textos viven en `public/locales/<lng>/common.json` → `t33`; el número de
 * canalizaciones de cada enseñanza se cuenta del catálogo (`ensenanzas33.js`),
 * nunca se escribe.
 */

/* Las tarjetas del 33 en el mundo, por grupo. Cada una es titulo + texto en
   los locales (`t33.m.<grupo>.<n>_t` / `_x`). Orden: lo que muchos creen
   (rotulo «En el mundo»), lo sagrado, el cuerpo y la Tierra, la Fundacion. */
const MUNDO = [
  /* Faina (14-sep): «Pon como primero en la lista lo que muchos creen y
     reemplaza ese titulo por En el mundo». Abre el bloque: primero lo que la
     gente cree del numero, despues lo que esta escrito.
     Faina (02-oct) lo renombró «En la numerología y el esoterismo»: dice desde
     el rótulo que lo de abajo es creencia, no enseñanza. */
  { grupo: 'creencia', n: 1, destacada: true },
  /* Faina (02-oct): el número maestro se desarrolla. La destacada queda con su
     frase (la escribió él) y debajo van tres tarjetas más de lo que la
     numerología cree del 33, con título, sin rótulo propio: son el mismo
     «En el mundo». Siempre dichas como creencia, nunca como enseñanza. */
  { grupo: 'creencia', desde: 2, n: 3, sinRotulo: true },
  { grupo: 'sagrado', n: 9 },
  /* Eran cuatro; Faina (14-sep) pidio tres y quitar una: salio el arsenico,
     el unico dato que no dice nada del alma. */
  { grupo: 'tierra', n: 3 },
  /* «En Chile» (la mina San José) salio el 14-sep: Faina lo sustituyo por «En
     la Fundación»: las 33 enseñanzas de Esenios, Ángeles y Arcángeles, que es
     la puerta al bloque de abajo. */
  { grupo: 'fundacion', n: 1, destacada: true }
];

const TreintaYTresPage = () => {
  const { t, i18n } = useTranslation();
  /* Con la enseñanza escrita, en español la ficha lleva SU título (la frase que
     la humanidad se lleva, Def 95); en los demás idiomas sigue el nombre corto
     traducido hasta que se traduzcan las 33 (Def 96). */
  const enEspanol = (i18n.language || 'es').startsWith('es');
  const { modo } = useModo();
  const { items: catalogo } = useCatalog();
  const { items: ensenanzas } = useTeachings();
  const conteo = countByTeaching(catalogo);

  return (
    <Box className={`fun fun-${modo} leer t33`}>
      {/* Solo el numero. Faina (14-sep) quito el lema («El 33 es el número de
          la Fundación. Jeshua vivió 33 años»), el parrafo de por que es
          nuestro y el rotulo «El 33 en el mundo»: la pagina entra directo en
          las tarjetas. */}
      <CabeceraSeccion titulo={t('t33.titulo')} />

      <Container maxWidth="lg">
        {MUNDO.map(({ grupo, n, destacada, desde = 1, sinRotulo }) => (
          /* La destacada —el numero maestro— va con la letra mas grande: Faina
             (14-sep) la quiere vista como lo mas importante del bloque. */
          <Box className={`t33-grupo${destacada ? ' t33-destacada' : ''}${sinRotulo ? ' t33-sigue' : ''}`} key={`${grupo}-${desde}`}>
            {!sinRotulo && (
              <Typography component="h3" className="t33-h3">{t(`t33.m.${grupo}.titulo`)}</Typography>
            )}
            <Box className="fun-reglas leer-lista t33-lista">
              {Array.from({ length: n }, (_, i) => (
                <Box className="fun-regla leer-sugerencia t33-tarjeta" key={i}>
                  {/* Las destacadas van solo con su frase, sin titulo (Faina,
                      14-sep: «El número maestro… bórralo»); no existe clave de
                      titulo para ellas, asi no hay que traducir un vacio. */}
                  {!destacada && (
                    <Typography component="span" className="leer-titulo">{t(`t33.m.${grupo}.${i + desde}_t`)}</Typography>
                  )}
                  <Typography component="p" className="leer-texto">{t(`t33.m.${grupo}.${i + desde}_x`)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}

        {/* LAS 33 ENSEÑANZAS — seis familias; cada enseñanza abre la
            Biblioteca con su código puesto como filtro. Sin rotulo ni bajada
            (Faina, 14-sep, los quito junto con la nota de verdad): la tarjeta
            «En la Fundación» de arriba ya las anuncia. */}
        {/* Faina (14-sep): «Yo las enseñanzas las pondría en formato
            canalización y las enumeraría del 1 al 33». Cada enseñanza es una
            ficha igual a las de la Biblioteca (`.bib-ficha`: barra, titulo,
            linea de voz, extracto) con su numero del 1 al 33 —seguido a traves
            de las seis familias—, la familia donde va la voz, la descripcion
            de la taxonomia donde va el extracto, y cuantas canalizaciones la
            enseñan. El codigo A1…F5 ya no se enseña: vive en el enlace. */}
        <Box className="bib-lista t33-fichas" id="ensenanzas">
          {/* Sin rotulos de familia (Faina, 14-sep: «borra los títulos en las
              enseñanzas, que aparezcan solo las enseñanzas»): una sola lista
              del 1 al 33. La familia sigue en la linea de cada ficha. */}
          {FAMILIAS.flatMap((f) => f.codigos.map((c) => ({ f, c }))).map(({ f, c }) => {
            const n = conteo[c] || 0;
            const numero = CODIGOS_33.indexOf(c) + 1;
            /* Def 93 (Faina, 14-sep): mientras la enseñanza no tiene su texto
               abre la Biblioteca filtrada; cuando lo tiene (la API la
               entrega en la lista de enseñanzas)
               abre su pagina /33/<n>, con sus canalizaciones debajo. */
            const escrita = ensenanzas.find((e) => e.numero === numero);
            return (
              <RouterLink
                key={c}
                to={escrita ? `/33/${numero}` : `/canalizaciones?ensenanza=${c}`}
                className={`bib-ficha t33-ficha${escrita ? ' t33-escrita' : ''}`}
              >
                <span className="bib-ficha-barra" />
                <span className="bib-ficha-cuerpo">
                  <span className="bib-ficha-titulo">
                    <span className="t33-numero">{numero}</span>{escrita && enEspanol ? formatoTitulo(escrita.titulo) : t(`t33.e.${c}`)}
                  </span>
                  <span className="bib-ficha-voz">
                    {t(f.clave)}
                    <span className="bib-ficha-quien"> · {n === 1 ? t('t33.ens_1') : t('t33.ens_n', { n })}</span>
                  </span>
                  <span className="bib-ficha-extracto">{t(`t33.d.${c}`)}</span>
                </span>
              </RouterLink>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default TreintaYTresPage;
